import { useState, useEffect } from 'react';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import { ICompositeScore } from '@nkr/models/composite-score-dashboard/composite-score';
import { ITceFormula } from '@nkr/models/composite-score-dashboard/tce-formula';
import { INcsScore } from '@nkr/models/composite-score-dashboard/ncs-score';

interface IResults {
    centerAlias: string,
    isPartnerCenter: boolean,
    compositeScore: ICompositeScore,
    tceFormula: ITceFormula,
    ncsScore: INcsScore
}

const initialResults: IResults = {
    centerAlias: '',
    isPartnerCenter: true,
    compositeScore: {
        acceptsAnyAnatomy: true,
        waitlistDeclineRatio: 0,
        familyVoucherDonations: 0,
        familyVoucherRedeemedDonations: 0,
        standardVoucherDonations: 0,
        compatibleTransplants: 0,
        remoteCompatibleDonors: 0,
        anyAnatomyDeclineRatioBonus: 0,
        compositeScore: 0
    },
    tceFormula: {
        networkPartnerCenterCount: 0,
        centerNcsScore: 0,
        networkNcsScore: 0,
        centerCompositeScore: 0,
        centerCompositeScorePercent: 0,
        networkCompositeScore: 0,
        compositeScoreRank: 0,
        centerForecastedFloat: 0,
        networkForecastedFloat: 0,
        centerShareOfFloat: 0,
        networkShareOfFloat: 0,
        shareOfFloatRank: 0,
        centerHardToPlaceDonorAddBacks: 0,
        networkHardToPlaceDonorAddBacks: 0,
        hardToPlaceDonorAddBacksRank: 0,
        centerTargetedChainEnds: 0,
        networkTargetedChainEnds: 0,
        targetedChainEndsRank: 0
    },
    ncsScore: {
        goodSamaritanNdd: 0,
        goodSamaritanFvd: 0,
        waitListChainEndTransplants: 0,
        kidneyReplacementCaseTransplants: 0,
        familyVoucherRedemptionTransplants: 0,
        pendingWaitListChainEndTransplants: 0,
        actualNcsScore: 0,
        forecastedNcsScore: 0
    }
}

interface IRetVal {
    results: IResults,
    loading: boolean,
    error: string
}

const useCenterCompositeScoreDashboard = (centerId: number): IRetVal => {
    const { dispatch } = useAppHookContext();

    const [results, setResults] = useState<IResults>(initialResults);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setLoading(true);
        setError('');

        const fetchData = async () => {
            try {
                const response = await dispatch(AuthActionCreators.apiGet({
                    url: `${__API__}/report/center-composite-score-dashboard?centerId=${centerId}`
                }));

                if (response && response.data.success) {
                    setResults(response.data.results);
                } else {
                    setError('Unable to load composite score data');
                }
            } catch (err) {
                setError('Unable to load composite score data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [centerId]);

    return { results, loading, error };
};

export default useCenterCompositeScoreDashboard;
